import { useState } from 'react';
import { formatDuration } from '@initiative/shared';
import type { MessageRendererProps } from '../types.js';
import { CastKnopf } from '../fernseher/CastKnopf.js';
import { FernsehKnopf } from '../fernseher/FernsehKnopf.js';
import { VideoWerkstatt } from '../video/VideoWerkstatt.js';
import { MediaCaption, PendingMedia } from './MediaFrame.js';
import { claimPlayback, mediaSrc, releasePlayback, standbildHolen } from './helpers.js';

/**
 * Video bubble – unscharfe Vorschau als Untergrund, darauf ein echtes Standbild.
 *
 * Kein `poster`: Das Plakat bliebe stehen, bis jemand abspielt, und es IST die
 * unscharfe Vorschau. Statt dessen liegt dieselbe Vorschau als `media-blur`
 * HINTER dem Video – sie füllt den Rahmen sofort und ohne schwarzes Aufblitzen –
 * und `standbildHolen` holt darüber das erste echte Bild.
 */
export function VideoBubble({ message, isMine }: MessageRendererProps) {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const attachment = message.attachments.find((item) => item.kind === 'video');

  if (!attachment) {
    return (
      <PendingMedia emoji="🎬" label="Video wird gesendet …" message={message} isMine={isMine} />
    );
  }

  const ratio =
    attachment.width && attachment.height ? `${attachment.width} / ${attachment.height}` : '16 / 9';

  return (
    <div className="media-bubble">
      <div className="media-frame" style={{ aspectRatio: ratio }}>
        {attachment.previewDataUrl && (
          <img className="media-blur" src={attachment.previewDataUrl} alt="" aria-hidden="true" />
        )}
        <video
          ref={setVideo}
          className="media-video"
          src={mediaSrc(attachment)}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={(ereignis) => standbildHolen(ereignis.currentTarget)}
          onPlay={(ereignis) => claimPlayback(ereignis.currentTarget)}
          onPause={(ereignis) => releasePlayback(ereignis.currentTarget)}
        />
        {/*
          „Auf den Fernseher" – immer da, auch ohne gefundenes Gerät (siehe
          `FernsehKnopf`). Daneben der echte Cast-Knopf, sobald jemand
          Chromecast erlaubt hat und ein Gerät da ist: §5.1 der Cast-
          Bedingungen verlangt ihn auf oberster Ebene an jedem castbaren
          Inhalt, und die Videoblase ist genau so einer. Vor der Zustimmung
          steht er nicht hier, sondern im Blatt hinter dem 📺 – zwei gleiche
          Zeichen, die Verschiedenes tun, wären die schlechtere Wahl.
        */}
        <FernsehKnopf video={video} anhang={attachment} />
        <CastKnopf
          stuecke={[attachment.id]}
          was={attachment.fileName ?? 'Dieses Video'}
          stil="blase"
        />
        {/*
          Bearbeiten und „GIF daraus machen" sitzen neben dem Fernsehknopf und
          nicht im Nachrichtenmenü: Wer ein Video sieht und etwas damit
          vorhat, sucht nicht erst unter „Weiterleiten" und „Löschen" danach.
        */}
        <VideoWerkstatt video={attachment} className="media-edit-btn" className2="media-gif-btn" />
        {attachment.durationMs != null && attachment.durationMs > 0 && (
          <span className="media-badge">{formatDuration(attachment.durationMs)}</span>
        )}
      </div>
      <MediaCaption body={message.body} isMine={isMine} />
    </div>
  );
}
